import type { CSSProperties, ReactElement, ReactNode } from "react"
import {
  MAX_WIDTH_CLASS,
  MAX_WIDTH_CLASS_FROM_LARGE,
  NEGATIVE_MARGIN_BOTTOM_CLASS,
  NEGATIVE_MARGIN_TOP_CLASS,
  PADDING_BACKGROUND,
  PADDING_BACKGROUND_FROM_LARGE,
  PADDING_GUTTER,
  PADDING_GUTTER_FROM_LARGE,
  PADDING_NONE,
  PADDING_NONE_FROM_LARGE,
} from "@gatsby-plugin-generic-page/components/BaseContainer/classNames"
import type { ContainerSettings } from "@gatsby-plugin-generic-page/fragments/containerSettings"
import {
  ContainerWidthType,
  MarginType,
} from "@gatsby-plugin-generic-page/fragments/containerSettings"
import getBackground from "@gatsby-plugin-generic-page/helpers/getBackground"
import { getCustomColor } from "@gatsby-plugin-generic-page/helpers/sectionColor"
import type { SpacingBase } from "@gatsby-plugin-generic-page/helpers/spacing"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"

export interface BaseContainerProps extends ContainerSettings {
  children: ReactNode
  className?: string
}

export default function BaseContainer({
  containerWidth,
  isFullWidthOnMobile,
  children,
  negativeMarginTop,
  negativeMarginBottom,
  backgroundColor,
  border,
  borderRadius,
  shadow,
  className,
  ...restProps
}: BaseContainerProps): ReactElement<BaseContainerProps> {
  const width: ContainerWidthType = !isEmpty(containerWidth)
    ? (containerWidth as ContainerWidthType)
    : ContainerWidthType.DEFAULT

  const marginTop: SpacingBase = !isEmpty(negativeMarginTop)
    ? negativeMarginTop
    : MarginType.STICKY
  const marginBottom: SpacingBase = !isEmpty(negativeMarginBottom)
    ? negativeMarginBottom
    : MarginType.STICKY

  const { customColor, udsBackgroundColorClass } =
    getBackground(backgroundColor)

  const customBackground = customColor?.background

  const resolvedBackground = customBackground
    ? getCustomColor(customBackground)
    : null

  const hasResolvedBackground = Boolean(
    resolvedBackground || udsBackgroundColorClass
  )

  const paddingClass =
    width === ContainerWidthType.FULL
      ? PADDING_NONE
      : hasResolvedBackground
        ? PADDING_BACKGROUND
        : PADDING_GUTTER

  const paddingClassFromLarge =
    width === ContainerWidthType.FULL
      ? PADDING_NONE_FROM_LARGE
      : hasResolvedBackground
        ? PADDING_BACKGROUND_FROM_LARGE
        : PADDING_GUTTER_FROM_LARGE

  const backgroundStyle: CSSProperties | undefined = resolvedBackground
    ? { background: resolvedBackground }
    : undefined

  return (
    <div
      {...restProps}
      style={backgroundStyle}
      className={cn(
        "w-full mx-auto",
        // Below `lg` the mobile-full-width variant replaces the width/padding
        // entirely, so only its `lg:` counterparts are emitted for the width.
        isFullWidthOnMobile
          ? cn(
              MAX_WIDTH_CLASS[ContainerWidthType.FULL],
              PADDING_NONE,
              MAX_WIDTH_CLASS_FROM_LARGE[width],
              paddingClassFromLarge
            )
          : cn(MAX_WIDTH_CLASS[width], paddingClass),
        NEGATIVE_MARGIN_TOP_CLASS[marginTop],
        NEGATIVE_MARGIN_BOTTOM_CLASS[marginBottom],
        udsBackgroundColorClass,
        !isEmpty(backgroundColor) && "overflow-hidden",
        border && border !== "none" && `border border--${border}`,
        borderRadius && borderRadius !== "none" && `rounded-${borderRadius}`,
        shadow && shadow !== "none" && `shadow-${shadow}`,
        className
      )}
    >
      {children}
    </div>
  )
}
